import { Modal, Select } from "antd";
import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { svg } from "../../../../Assets/svg";
import * as Yup from "yup";
import { useFormik } from "formik";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch } from "../../../../store";
import {
  createCustomerAdditionalContactsAPI,
  updateCustomerAdditionalContactAPI,
} from "../clientDistributorsApis";
import { decryptByText } from "../../../../helpers/bycrypt";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";

interface Props {
  open: boolean;
  clientid: string;
  onClose: () => void;
  refreshData: () => void;
  editData?: any | null;
  existingContacts?: any[];
}

const CONTACT_TYPE_OPTIONS = [
  { label: "Default Contact", value: "isDefaultContact" },
  { label: "Default Account Contact", value: "isDefaultAccountContact" },
];

const contactSchema = Yup.object().shape({
  first_name: Yup.string().required("First Name is required"),
  last_name: Yup.string().required("Last Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .required("Phone Number is required")
    .matches(/^[0-9+\-\s()]*$/, "Only numbers and symbols (+ - ) are allowed")
    .test("phone-too-short", "Phone number is too short", (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, "");
      return digits.length >= 7;
    })
    .test("phone-too-long", "Phone number is too long", (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, "");
      return digits.length <= 15;
    }),
  department: Yup.string().nullable(),
  contact_type: Yup.array().of(Yup.string()).nullable(),
});

const ClientAdditionalContactsFormModal = ({
  open,
  clientid,
  onClose,
  refreshData,
  editData,
  existingContacts = [],
}: Props) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const orderId = searchParams.get("order_id");
  const contextualParams = quoteId
    ? { quote_id: quoteId }
    : orderId
    ? { order_id: orderId }
    : undefined;
  const [saving, setSaving] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictInfo, setConflictInfo] = useState<{
    title: string;
    subTitle: string;
  }>({
    title: "",
    subTitle: "",
  });
  const [pendingValues, setPendingValues] = useState<any>(null);

  const getInitialContactTypes = (contact: any) => {
    const types: string[] = [];
    if (contact?.default_contact || contact?.isDefaultContact) {
      types.push("isDefaultContact");
    }
    if (contact?.default_account_contact || contact?.isDefaultAccountContact) {
      types.push("isDefaultAccountContact");
    }
    return types;
  };

  const initialValues = useMemo(() => {
    return {
      additional_contacts: editData
        ? [
            {
              id: editData?.id || editData?._id || uuidv4(),
              first_name: editData?.first_name
                ? decryptByText(editData?.first_name)
                : "",
              last_name: editData?.last_name
                ? decryptByText(editData?.last_name)
                : "",
              email: editData?.email ? decryptByText(editData?.email) : "",
              phone: editData?.phone ? editData?.phone : "",
              department: editData?.department ? editData?.department : "",
              contact_type: getInitialContactTypes(editData),
            },
          ]
        : [
            {
              id: uuidv4(),
              first_name: "",
              last_name: "",
              email: "",
              phone: "",
              department: "",
              contact_type: [],
            },
          ],
    };
  }, [editData, open]);

  const executeSave = (values: any) => {
    setSaving(true);

    if (editData) {
      const contact = values?.additional_contacts?.[0];
      const isDefaultContact =
        contact?.contact_type?.includes("isDefaultContact") || false;
      const isDefaultAccountContact =
        contact?.contact_type?.includes("isDefaultAccountContact") || false;

      const payload = {
        customer_id: clientid,
        contact_id: editData?._id,
        first_name: contact?.first_name,
        last_name: contact?.last_name,
        email: contact?.email,
        phone: contact?.phone,
        department: contact?.department,
        isDefaultContact,
        isDefaultAccountContact,
      };
      dispatch(
        updateCustomerAdditionalContactAPI({
          payload,
          params: contextualParams,
          onSuccess: () => {
            setSaving(false);
            setConflictModalOpen(false);
            refreshData();
            onClose();
          },
          onError: () => {
            setSaving(false);
          },
        }),
      );
    } else {
      const payload = {
        id: clientid,
        additional_contacts: values.additional_contacts.map(
          (contact: any) => ({
            ...(editData && editData._id ? { id: editData._id } : {}),
            first_name: contact.first_name,
            last_name: contact.last_name,
            email: contact.email,
            ...(contact.phone ? { phone: contact.phone } : {}),
            ...(contact.department ? { department: contact.department } : {}),
            isDefaultContact:
              contact?.contact_type?.includes("isDefaultContact") || false,
            isDefaultAccountContact:
              contact?.contact_type?.includes("isDefaultAccountContact") ||
              false,
          }),
        ),
      };
      dispatch(
        createCustomerAdditionalContactsAPI({
          payload,
          params: contextualParams,
          onSuccess: () => {
            setSaving(false);
            setConflictModalOpen(false);
            refreshData();
            onClose();
          },
          onError: () => {
            setSaving(false);
          },
        }),
      );
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,

    validationSchema: Yup.object({
      additional_contacts: Yup.array()
        .of(contactSchema)
        .test(
          "unique-default-contact",
          "Only one contact can be marked as Default Contact",
          (contacts) => {
            if (!contacts) return true;
            const count = contacts.filter((c: any) =>
              c.contact_type?.includes("isDefaultContact"),
            ).length;
            return count <= 1;
          },
        )
        .test(
          "unique-default-account-contact",
          "Only one contact can be marked as Default Account Contact",
          (contacts) => {
            if (!contacts) return true;
            const count = contacts.filter((c: any) =>
              c.contact_type?.includes("isDefaultAccountContact"),
            ).length;
            return count <= 1;
          },
        ),
    }),
    onSubmit: (values) => {
      const isSettingDefaultContact = values.additional_contacts.some(
        (c: any) => c.contact_type?.includes("isDefaultContact"),
      );
      const isSettingDefaultAccountContact = values.additional_contacts.some(
        (c: any) => c.contact_type?.includes("isDefaultAccountContact"),
      );

      const currentEditingId = editData?._id || editData?.id;

      const hasExistingDefaultContact = existingContacts?.some((c: any) => {
        if (
          currentEditingId &&
          (c?._id === currentEditingId || c?.id === currentEditingId)
        ) {
          return false;
        }
        return Boolean(c?.default_contact || c?.isDefaultContact);
      });

      const hasExistingDefaultAccountContact = existingContacts?.some(
        (c: any) => {
          if (
            currentEditingId &&
            (c?._id === currentEditingId || c?.id === currentEditingId)
          ) {
            return false;
          }
          return Boolean(
            c?.default_account_contact || c?.isDefaultAccountContact,
          );
        },
      );

      const defaultContactConflict =
        isSettingDefaultContact && hasExistingDefaultContact;
      const defaultAccountContactConflict =
        isSettingDefaultAccountContact && hasExistingDefaultAccountContact;

      if (defaultContactConflict && defaultAccountContactConflict) {
        setConflictInfo({
          title: "Replace Default Contacts, Are You Sure?",
          subTitle:
            "A Default Contact and a Default Account Contact already exist for this client. Setting this contact as default will replace the existing ones. Please confirm before proceeding.",
        });
        setPendingValues(values);
        setConflictModalOpen(true);
      } else if (defaultContactConflict) {
        setConflictInfo({
          title: "Replace Default Contact, Are You Sure?",
          subTitle:
            "A Default Contact already exists for this client. Setting this contact as the Default Contact will replace the existing one. Please confirm before proceeding.",
        });
        setPendingValues(values);
        setConflictModalOpen(true);
      } else if (defaultAccountContactConflict) {
        setConflictInfo({
          title: "Replace Default Account Contact, Are You Sure?",
          subTitle:
            "A Default Account Contact already exists for this client. Setting this contact as the Default Account Contact will replace the existing one. Please confirm before proceeding.",
        });
        setPendingValues(values);
        setConflictModalOpen(true);
      } else {
        executeSave(values);
      }
    },
  });

  const handleAddMore = () => {
    formik.setFieldValue("additional_contacts", [
      ...formik.values.additional_contacts,
      {
        id: uuidv4(),
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        department: "",
        contact_type: [],
      },
    ]);
  };

  const handleRemove = (idToRemove: string) => {
    const updatedContacts = formik.values.additional_contacts.filter(
      (contact) => contact.id !== idToRemove,
    );
    formik.setFieldValue("additional_contacts", updatedContacts);
  };

  return (
    <>
      <Modal
        open={open}
        centered
        width={"33.75rem"}
        footer={null}
        onCancel={onClose}
        closeIcon={svg?.closeIcon}
        title={
          <div className="text-center fs-4 fw-bolder lh-m">
            {editData
              ? "Update Additional Contact"
              : "Create Additional Contacts"}
          </div>
        }
        className="commonModalClass"
      >
        <form className="page-form" onSubmit={formik.handleSubmit}>
          <div
            className="pe-2"
            style={{ maxHeight: "500px", overflowY: "auto", overflowX: "hidden" }}
          >
            {formik.values.additional_contacts.map((contact, index) => {
              // Helper to check for errors
              const contactErrors =
                (formik.errors.additional_contacts as any)?.[index] || {};
              const contactTouched =
                (formik.touched.additional_contacts as any)?.[index] || {};

              return (
                <div
                  key={contact.id}
                  className="border rounded p-3 mb-3 position-relative"
                >
                  {/* Show remove button only in Create mode and if more than one exists */}
                  {!editData && formik.values.additional_contacts.length > 1 && (
                    <div
                      className="position-absolute top-0 end-0 mt-2 me-2 cursor-pointer text-danger svg-20"
                      style={{ zIndex: 10 }}
                      onClick={() => handleRemove(contact.id)}
                    >
                      {svg?.deleteIcon}
                    </div>
                  )}

                  <div className="row gy-3">
                    <div className="col-md-6">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          contactErrors.first_name && contactTouched.first_name
                            ? "is-invalid"
                            : ""
                        }`}
                        name={`additional_contacts[${index}].first_name`}
                        value={contact.first_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {contactErrors.first_name && contactTouched.first_name && (
                        <div className="invalid-feedback">
                          {contactErrors.first_name}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className={`form-control ${
                          contactErrors.last_name && contactTouched.last_name
                            ? "is-invalid"
                            : ""
                        }`}
                        name={`additional_contacts[${index}].last_name`}
                        value={contact.last_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {contactErrors.last_name && contactTouched.last_name && (
                        <div className="invalid-feedback">
                          {contactErrors.last_name}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className={`form-control ${
                          contactErrors.email && contactTouched.email
                            ? "is-invalid"
                            : ""
                        }`}
                        name={`additional_contacts[${index}].email`}
                        value={contact.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {contactErrors.email && contactTouched.email && (
                        <div className="invalid-feedback">
                          {contactErrors.email}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input
                        type="text"
                        className={`form-control ${
                          contactErrors.phone && contactTouched.phone
                            ? "is-invalid"
                            : ""
                        }`}
                        name={`additional_contacts[${index}].phone`}
                        value={contact.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {contactErrors.phone && contactTouched.phone && (
                        <div className="invalid-feedback">
                          {contactErrors.phone}
                        </div>
                      )}
                    </div>

                    <div className="col-12">
                      <label className="form-label">Contact Type</label>
                      <Select
                        mode="multiple"
                        allowClear
                        className="w-100 contact-type-select"
                        placeholder="Select Contact Type"
                        suffixIcon={svg?.selectDownArrow}
                        maxTagCount="responsive"
                        value={contact.contact_type}
                        onChange={(values) => {
                          formik.setFieldValue(
                            `additional_contacts[${index}].contact_type`,
                            values,
                          );
                        }}
                        options={CONTACT_TYPE_OPTIONS}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <style>{`
            .contact-type-select.ant-select-multiple,
            .contact-type-select .ant-select-selector {
              min-height: 38px !important;
              padding: 2px  !important;
            }
            .contact-type-select .ant-select-selection-placeholder {
              line-height: 32px !important;
            }
            .contact-type-select .ant-select-selection-item {
              height: 26px !important;
              line-height: 24px !important;
              margin-block: 2px !important;
            }
          `}</style>

          {typeof formik.errors.additional_contacts === "string" && (
            <div className="text-danger mt-2 mb-2 text-center">
              {formik.errors.additional_contacts}
            </div>
          )}

          {!editData && (
            <div className="d-flex justify-content-end mb-3">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleAddMore}
              >
                + Add More
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center gap-3 mt-4">
            <button
              onClick={onClose}
              className="btn btn-lg btn-outline-secondary px-5"
              type="button"
            >
              CLOSE
            </button>
            <button
              type="submit"
              className="btn btn-lg btn-primary px-5"
              disabled={saving}
            >
              {saving ? "SAVING..." : editData ? "UPDATE" : "SAVE"}
            </button>
          </div>
        </form>
      </Modal>

      <CommonConfirmationModal
        open={conflictModalOpen}
        title={conflictInfo.title}
        subTitle={conflictInfo.subTitle}
        loading={saving}
        confirmButtonText="Proceed"
        confirmButtonColorClass="btn-primary"
        onSuccess={() => {
          if (pendingValues) {
            executeSave(pendingValues);
          }
        }}
        onClose={() => setConflictModalOpen(false)}
      />
    </>
  );
};

export default ClientAdditionalContactsFormModal;
